import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Package, PoundSterling, Truck, LogOut, RefreshCw } from "lucide-react";
import { formatPrice } from "@/data/products";
import { AdminProducts } from "@/components/site/AdminProducts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tiger_admin_token";
const STATUSES = ["unfulfilled", "processing", "shipped", "delivered"];

const STATUS_STYLES = {
  unfulfilled: "bg-tiger/10 text-tiger border-tiger/40",
  processing: "bg-zinc-900 text-white border-zinc-900",
  shipped: "bg-zinc-200 text-zinc-800 border-zinc-300",
  delivered: "bg-white text-zinc-500 border-zinc-300",
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("orders");

  const fetchOrders = useCallback(
    async (tok) => {
      try {
        const { data } = await axios.get(`${API}/admin/orders`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        setData(data);
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
        } else {
          toast.error("Could not load orders.");
        }
      }
    },
    []
  );

  useEffect(() => {
    if (token) fetchOrders(token);
  }, [token, fetchOrders]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, status) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Order ${orderId} marked as ${status}`);
      fetchOrders(token);
    } catch {
      toast.error("Status update failed.");
    }
  };

  const handlePushPrintful = async (orderId) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${orderId}/push-printful`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.printful_status === "draft_created") {
        toast.success(`Order ${orderId} pushed to Printful`);
      } else {
        toast.info(`Printful status: ${data.printful_status.replace(/_/g, " ")}`);
      }
      fetchOrders(token);
    } catch {
      toast.error("Printful push failed.");
    }
  };

  const handleSyncPrintful = async () => {
    try {
      await axios.post(
        `${API}/admin/orders/sync-printful`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Printful order statuses synced");
      fetchOrders(token);
    } catch (err) {
      toast.info(err.response?.data?.detail || "Printful sync unavailable");
    }
  };

  const handleSyncCatalogue = async () => {
    try {
      const { data } = await axios.post(
        `${API}/admin/printful/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Catalogue synced — ${data.synced} product(s) pulled from Printful`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Catalogue sync failed");
    }
  };

  const handleRemoveSamples = async () => {
    if (!window.confirm("Remove all sample products from the shop? This cannot be undone.")) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/products/remove-samples`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Removed ${data.removed} sample product(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-fog px-4 font-body" data-testid="admin-login-page">
        <div className="w-full max-w-sm border border-zinc-200 bg-white p-8">
          <img src="/assets/logo-black.png" alt="Tiger Clothing" className="mx-auto mb-6 h-16 w-auto" />
          <h1 className="text-center font-heading text-lg font-bold uppercase tracking-widest text-zinc-900">Admin Login</h1>
          <p className="mt-1 text-center font-body text-xs text-zinc-500">Orders & fulfilment dashboard</p>
          <form onSubmit={handleLogin} className="mt-6 space-y-4" data-testid="admin-login-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@tiger-clothing.co.uk"
              data-testid="admin-email-input"
              className="w-full border border-zinc-300 bg-white px-4 py-3 font-body text-sm text-zinc-900 outline-none focus:border-tiger"
            />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="admin-password-input"
              className="w-full border border-zinc-300 bg-white px-4 py-3 font-body text-sm text-zinc-900 outline-none focus:border-tiger"
            />
            {error && (
              <p className="font-body text-xs font-semibold text-red-600" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-btn"
              className="w-full bg-zinc-900 py-3 font-heading text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-tiger hover:text-black disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
          <Link to="/gb/en/home.html" className="mt-6 block text-center font-body text-xs text-zinc-400 hover:text-tiger" data-testid="admin-back-home-link">
            ← Back to store
          </Link>
        </div>
      </div>
    );
  }

  const stats = data?.stats;

  return (
    <div className="min-h-screen bg-fog font-body text-zinc-900" data-testid="admin-dashboard">
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <img src="/assets/logo-black.png" alt="Tiger Clothing" className="h-10 w-auto" />
            <span className="font-heading text-sm font-bold uppercase tracking-widest">Orders Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSyncCatalogue}
              data-testid="admin-sync-catalogue-btn"
              className="border border-zinc-300 px-4 py-2 font-heading text-xs font-bold uppercase tracking-widest text-zinc-600 hover:border-tiger hover:text-tiger"
            >
              Sync Catalogue
            </button>
            <button
              onClick={handleRemoveSamples}
              data-testid="admin-remove-samples-btn"
              className="border border-red-300 px-4 py-2 font-heading text-xs font-bold uppercase tracking-widest text-red-600 hover:bg-red-600 hover:text-white"
            >
              Remove Samples
            </button>
            <button
              onClick={handleSyncPrintful}
              data-testid="admin-sync-printful-btn"
              className="border border-zinc-300 px-4 py-2 font-heading text-xs font-bold uppercase tracking-widest text-zinc-600 hover:border-tiger hover:text-tiger"
            >
              Sync Printful
            </button>
            <button
              onClick={() => fetchOrders(token)}
              data-testid="admin-refresh-btn"
              className="flex h-9 w-9 items-center justify-center border border-zinc-300 text-zinc-600 hover:border-tiger hover:text-tiger"
              aria-label="Refresh orders"
            >
              <RefreshCw size={15} />
            </button>
            <Link to="/gb/en/home.html" className="border border-zinc-300 px-4 py-2 font-heading text-xs font-bold uppercase tracking-widest text-zinc-600 hover:border-tiger hover:text-tiger" data-testid="admin-store-link">
              Store
            </Link>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-1.5 bg-zinc-900 px-4 py-2 font-heading text-xs font-bold uppercase tracking-widest text-white hover:bg-tiger hover:text-black"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        <div className="mb-8 flex gap-2" data-testid="admin-tabs">
          {["orders", "products"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`px-6 py-2.5 font-heading text-xs font-bold uppercase tracking-widest transition-colors ${
                tab === t ? "bg-zinc-900 text-white" : "border border-zinc-300 text-zinc-500 hover:border-tiger hover:text-tiger"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "products" && <AdminProducts token={token} />}

        {tab === "orders" && (
        <>
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="admin-stats">
          <div className="flex items-center gap-4 border border-zinc-200 bg-white p-5">
            <span className="flex h-11 w-11 items-center justify-center bg-tiger/10 text-tiger"><Package size={20} /></span>
            <div>
              <p className="font-display text-2xl" data-testid="stat-total-orders">{stats?.total_orders ?? 0}</p>
              <p className="font-body text-xs uppercase tracking-widest text-zinc-500">Paid Orders</p>
            </div>
          </div>
          <div className="flex items-center gap-4 border border-zinc-200 bg-white p-5">
            <span className="flex h-11 w-11 items-center justify-center bg-tiger/10 text-tiger"><PoundSterling size={20} /></span>
            <div>
              <p className="font-display text-2xl" data-testid="stat-revenue">{formatPrice(stats?.revenue ?? 0)}</p>
              <p className="font-body text-xs uppercase tracking-widest text-zinc-500">Revenue</p>
            </div>
          </div>
          <div className="flex items-center gap-4 border border-zinc-200 bg-white p-5">
            <span className="flex h-11 w-11 items-center justify-center bg-tiger/10 text-tiger"><Truck size={20} /></span>
            <div>
              <p className="font-display text-2xl" data-testid="stat-awaiting">{stats?.awaiting_fulfillment ?? 0}</p>
              <p className="font-body text-xs uppercase tracking-widest text-zinc-500">Awaiting Fulfilment</p>
            </div>
          </div>
        </div>

        {!data ? (
          <p className="py-20 text-center font-body text-sm text-zinc-500" data-testid="orders-loading">Loading orders...</p>
        ) : data.orders.length === 0 ? (
          <div className="border border-zinc-200 bg-white py-20 text-center" data-testid="orders-empty">
            <img src="/assets/logo-black.png" alt="" className="mx-auto mb-4 w-16 opacity-30" />
            <p className="font-body text-sm text-zinc-500">No paid orders yet. They'll appear here the moment a customer checks out.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-zinc-200 bg-white" data-testid="orders-table">
            <table className="w-full min-w-[760px] font-body text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-[10px] uppercase tracking-widest text-zinc-400">
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Items & Sizes</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Printful</th>
                  <th className="px-4 py-3">Fulfilment</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.map((order) => (
                  <tr key={order.order_id} className="border-b border-zinc-100 align-top" data-testid={`order-row-${order.order_id}`}>
                    <td className="px-4 py-3 font-heading text-xs font-bold">{order.order_id}</td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {new Date(order.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-600">{order.customer_email || "—"}</td>
                    <td className="px-4 py-3">
                      <ul className="space-y-1">
                        {order.items.map((item, i) => (
                          <li key={i} className="text-xs text-zinc-700">
                            <span className="font-semibold">{item.title}</span>
                            {item.sizes ? <span className="text-zinc-500"> — {item.sizes}</span> : null}
                            <span className="text-zinc-400"> (x{item.quantity})</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-4 py-3 text-right font-heading text-sm font-bold text-tiger">{formatPrice(order.amount)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block border px-2 py-1 font-heading text-[10px] font-bold uppercase tracking-widest ${
                          order.printful_status === "draft_created"
                            ? "border-zinc-900 bg-zinc-900 text-white"
                            : "border-zinc-300 bg-white text-zinc-500"
                        }`}
                        data-testid={`printful-status-${order.order_id}`}
                      >
                        {(order.printful_status || "not_connected").replace(/_/g, " ")}
                      </span>
                      {order.printful_status !== "draft_created" && (
                        <button
                          onClick={() => handlePushPrintful(order.order_id)}
                          data-testid={`printful-push-${order.order_id}`}
                          className="ml-2 border border-tiger px-2 py-1 font-heading text-[10px] font-bold uppercase tracking-widest text-tiger hover:bg-tiger hover:text-black"
                        >
                          Push
                        </button>
                      )}
                      {order.tracking?.number && (
                        <a
                          href={order.tracking.url || "#"}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`tracking-link-${order.order_id}`}
                          className="mt-1 block font-body text-[10px] text-zinc-500 underline hover:text-tiger"
                        >
                          {order.tracking.carrier || "Carrier"} · {order.tracking.number} →
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={order.fulfillment_status}
                        onChange={(e) => handleStatusChange(order.order_id, e.target.value)}
                        data-testid={`fulfillment-select-${order.order_id}`}
                        className={`border px-2.5 py-1.5 font-heading text-[10px] font-bold uppercase tracking-widest outline-none ${STATUS_STYLES[order.fulfillment_status] || ""}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
